/**
 * THE ORGANIC CAVE'S PLAN: where the rock is, which of it carries iron, and what is sealed inside it.
 *
 * The cave is solid basalt you dig through, not a carved maze: one arrival chamber round the landing, a ring of
 * bedrock round the edge, and everything else breakable rock. Inside the rock are SEALED POCKETS -- natural
 * grottos with crystals and still pools, invisible until a swing breaks into one; the deepest are the ember
 * cave's VOIDS (the armory, the armed dead) -- and ORE VEINS, wandering lines that tend to run toward a pocket,
 * so following a vein is how you find one. Each vein is one mineral, laid at the mining depth the ember mine
 * promises it (coal near, gold deep) and sized to pay the mine's minimum yields exactly: the ore you can see is
 * the ore that pays (organicMineRewards).
 *
 * Pure and seeded: the same (width, height, landing, seed) always plans the same cave. The server turns the plan
 * into rock tiles (organic_cave_service.ts); the client draws the rock from the `organicRock` layer it becomes
 * and reads the veins/crystals/pools from the room's `organicCave` property (rendering/impl/pixijs/rock_mass.ts).
 */

/** One character per cell of the `organicRock` layer. */
export const OrganicRock = {
    /** Open from the start: the arrival chamber. */
    Open: "0",
    Rock: "1",
    /** Rock with iron in it (and below, the other ores). */
    Iron: "2",
    /** The unbreakable ring round the edge. */
    Bedrock: "3",
    /** Open from the start but sealed in the rock: a pocket's floor. */
    Pocket: "4",
    /** The way home: unbreakable rock with an opening drawn in its face (the exit region sits in front of it). */
    Mouth: "5",
    Coal: "6",
    Copper: "7",
    Silver: "8",
    Gold: "9",
} as const

/** The ember mine's minerals (game-common/map/map OreMaterial). */
export type OrganicOre = "coal" | "iron" | "copper" | "silver" | "gold"
export const ORGANIC_ORE_CODES: Record<OrganicOre, string> = {
    coal: OrganicRock.Coal, iron: OrganicRock.Iron, copper: OrganicRock.Copper, silver: OrganicRock.Silver, gold: OrganicRock.Gold,
}
/** The mineral in a cell of the `organicRock` layer, if any. */
export const organicOreAt = (code: string | undefined): OrganicOre | undefined =>
    code === OrganicRock.Coal ? "coal" : code === OrganicRock.Iron ? "iron" : code === OrganicRock.Copper ? "copper"
        : code === OrganicRock.Silver ? "silver" : code === OrganicRock.Gold ? "gold" : undefined

/** The room JSON layer the plan travels in, and the property on it that carries the features. */
export const ORGANIC_ROCK_LAYER = "organicRock"
export const ORGANIC_CAVE_PROPERTY = "organicCave"

/** Solid in the drawn mass (and in collision): rock, iron, bedrock, the mouth. */
export const organicRockIsSolid = (code: string | undefined): boolean =>
    code === undefined || organicRockIsBreakable(code) || code === OrganicRock.Bedrock || code === OrganicRock.Mouth
/** Breakable: what becomes a rock tile. */
export const organicRockIsBreakable = (code: string | undefined): boolean =>
    code === OrganicRock.Rock || organicOreAt(code) !== undefined

export interface OrganicCavePoint {
    x: number
    y: number
}

/** What the client needs beyond the per-cell layer, in CELL units. Sent as the room's `organicCave` property. */
export interface OrganicCaveFeatures {
    faceHeight: number
    /** Each vein's line, for the flecks the client draws along it; its cells are coded in the layer. */
    veins: Array<{ material: OrganicOre; points: OrganicCavePoint[] }>
    crystals: Array<OrganicCavePoint & { scale: number }>
    pools: Array<OrganicCavePoint & { rx: number; ry: number }>
}

/** A sealed pocket: its centre, its floor, and whether it is one of the ember cave's voids. */
export interface OrganicPocket extends OrganicCavePoint {
    cells: OrganicCavePoint[]
    void: boolean
}

export interface OrganicCavePlan extends OrganicCaveFeatures {
    width: number
    height: number
    /** [x][y], like every other tile layer. */
    cells: string[][]
    /** Cell the player lands in (the chamber's centre). */
    landing: OrganicCavePoint
    /** Deepest first. */
    pockets: OrganicPocket[]
}

/**
 * The veins a cave grows, in the order they are laid: depth bands are fractions of the deepest mining route
 * (the ember mine's seams, DEFAULT_EMBER_MINE), `cells` is how many ore cells the vein claims, each paying one.
 * Sized to the mine's minimum yield (coal 12, iron 9, copper 6, silver 3, gold 3) with a little over.
 */
export const ORGANIC_VEINS: Array<{ material: OrganicOre; cells: number; minDepth: number; maxDepth: number; entrance?: boolean }> = [
    { material: "coal", cells: 4, minDepth: 0, maxDepth: 1, entrance: true },
    { material: "gold", cells: 4, minDepth: 0.75, maxDepth: 1 },
    { material: "silver", cells: 4, minDepth: 0.5, maxDepth: 1 },
    { material: "copper", cells: 7, minDepth: 0.2, maxDepth: 0.8 },
    { material: "iron", cells: 10, minDepth: 0.1, maxDepth: 0.65 },
    { material: "coal", cells: 9, minDepth: 0.05, maxDepth: 0.45 },
]
export const ORGANIC_VOID_COUNT = 2

/** How close a cell centre must be to a vein's line to carry its iron, in cells. */
export const ORGANIC_VEIN_REACH = 0.85
export const ORGANIC_FACE_HEIGHT = 52

const seededRandom = (seed: string): (() => number) => {
    let h = 1779033703 ^ seed.length
    for (let i = 0; i < seed.length; i++) {
        h = Math.imul(h ^ seed.charCodeAt(i), 3432918353)
        h = (h << 13) | (h >>> 19)
    }
    let a = h >>> 0
    return () => {
        a = (a + 0x6d2b79f5) | 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

/** Distance from (px, py) to a polyline, in the polyline's units. */
export const distanceToPolyline = (px: number, py: number, line: OrganicCavePoint[]): number => {
    let best = Infinity
    for (let i = 0; i + 1 < line.length; i++) {
        const a = line[i], b = line[i + 1]
        const dx = b.x - a.x, dy = b.y - a.y
        const len = dx * dx + dy * dy
        const t = len ? Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / len)) : 0
        best = Math.min(best, Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy)))
    }
    return best
}

/** A blob of cells round (cx, cy): an ellipse whose radius wanders with the angle, so no two look alike. */
const blob = (cx: number, cy: number, rx: number, ry: number, random: () => number): OrganicCavePoint[] => {
    const p1 = random() * Math.PI * 2, p2 = random() * Math.PI * 2
    const a1 = 0.14 + random() * 0.12, a2 = 0.06 + random() * 0.08
    const out: OrganicCavePoint[] = []
    for (let y = Math.floor(cy - ry - 2); y <= Math.ceil(cy + ry + 2); y++) {
        for (let x = Math.floor(cx - rx - 2); x <= Math.ceil(cx + rx + 2); x++) {
            const dx = (x + 0.5 - cx) / rx, dy = (y + 0.5 - cy) / ry
            const theta = Math.atan2(dy, dx)
            const reach = 1 + a1 * Math.sin(3 * theta + p1) + a2 * Math.sin(5 * theta + p2)
            if (Math.hypot(dx, dy) < reach) out.push({ x, y })
        }
    }
    return out
}

/**
 * `mouth` is the left of the two cells the way home is drawn in (CaveMapBuilder's exit); the chamber is cut
 * just below it so the first thing the player sees is the way back.
 */
export const planOrganicCave = (width: number, height: number, landing: OrganicCavePoint, seed: string,
    mouth?: OrganicCavePoint, voids: number = ORGANIC_VOID_COUNT): OrganicCavePlan => {
    const random = seededRandom(seed)
    const cells: string[][] = Array.from({ length: width }, (_, x) =>
        Array.from({ length: height }, (_, y) =>
            x === 0 || y === 0 || x === width - 1 || y === height - 1 ? OrganicRock.Bedrock : OrganicRock.Rock))
    const inside = (x: number, y: number, pad = 1) => x >= pad && y >= pad && x < width - pad && y < height - pad
    const lx = Math.max(5, Math.min(width - 6, Math.floor(landing.x))), ly = Math.max(5, Math.min(height - 6, Math.floor(landing.y)))
    const centre = { x: lx + 0.5, y: ly + 0.5 }

    // The arrival chamber: room to stand and swing in, its walls in every direction.
    for (const c of blob(centre.x, centre.y, 4.4, 3.3, random)) {
        if (inside(c.x, c.y, 2)) cells[c.x][c.y] = OrganicRock.Open
    }
    cells[lx][ly] = OrganicRock.Open
    if (mouth) {
        // The mouth sits in a wall: no chamber cut above or beside it.
        for (let y = 1; y <= mouth.y; y++) for (let x = mouth.x - 2; x <= mouth.x + 3; x++) {
            if (inside(x, y, 1) && cells[x][y] === OrganicRock.Open) cells[x][y] = OrganicRock.Rock
        }
        // The mouth's face, and the ground in front of it that walks into the exit region.
        for (const dx of [0, 1]) {
            if (!inside(mouth.x + dx, mouth.y, 1)) continue
            cells[mouth.x + dx][mouth.y] = OrganicRock.Mouth
            for (let dy = 1; dy <= 2; dy++) {
                if (inside(mouth.x + dx, mouth.y + dy, 1)) cells[mouth.x + dx][mouth.y + dy] = OrganicRock.Open
            }
        }
    }

    // Sealed pockets, never touching the chamber or each other.
    const placed: Array<OrganicCavePoint & { r: number; cells: OrganicCavePoint[] }> = []
    const wanted = Math.max(voids + 1, Math.round((width * height) / 450))
    for (let tries = 0; tries < 600 && placed.length < wanted; tries++) {
        const r = 1.8 + random() * 1.5
        const x = 3 + r + random() * (width - 6 - 2 * r), y = 3 + r + random() * (height - 6 - 2 * r)
        if (Math.hypot(x - centre.x, y - centre.y) < 9 + r) continue
        if (placed.some(p => Math.hypot(p.x - x, p.y - y) < p.r + r + 3)) continue
        const shape = blob(x, y, r * (1.1 + random() * 0.3), r * (0.75 + random() * 0.2), random).filter(c => inside(c.x, c.y, 2))
        const sealed = shape.every(c => {
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                const v = cells[c.x + dx]?.[c.y + dy]
                if (v === OrganicRock.Open || v === OrganicRock.Pocket) return false
            }
            return true
        })
        if (!sealed || shape.length < 9) continue
        shape.forEach(c => (cells[c.x][c.y] = OrganicRock.Pocket))
        placed.push({ x, y, r, cells: shape })
    }

    // Mining depth: blocks to break from the landing (open ground and pockets cost nothing), as the mine counts it.
    const depth = miningDepths(cells, width, height, { x: lx, y: ly })
    const deepest = Math.max(1, ...depth.filter(d => d < Infinity))
    const depthOf = (x: number, y: number) => depth[y * width + x]

    // The deepest pockets are the voids. A pocket's depth is the cheapest way into it.
    const pocketDepth = (p: { cells: OrganicCavePoint[] }) => Math.min(...p.cells.map(c => depthOf(c.x, c.y)))
    const pockets: OrganicPocket[] = placed
        .map(p => ({ x: p.x, y: p.y, cells: p.cells, void: false }))
        .sort((a, b) => pocketDepth(b) - pocketDepth(a))
    pockets.slice(0, voids).forEach(p => (p.void = true))

    // Veins: each grows from a start in its depth band, drifting toward a pocket, claiming rock in band until it
    // holds its cells. A vein that cannot find room in its band is tried again from elsewhere.
    const veins: OrganicCaveFeatures["veins"] = []
    for (const spec of ORGANIC_VEINS) {
        const code = ORGANIC_ORE_CODES[spec.material]
        const inBand = (x: number, y: number) => {
            const d = depthOf(x, y)
            if (!(d < Infinity) || cells[x][y] !== OrganicRock.Rock) return false
            if (spec.entrance) return d <= 3
            return d / deepest >= spec.minDepth && d / deepest <= spec.maxDepth
        }
        const starts: OrganicCavePoint[] = []
        for (let y = 2; y < height - 2; y++) for (let x = 2; x < width - 2; x++) if (inBand(x, y)) starts.push({ x, y })
        for (let attempt = 0; attempt < 24 && starts.length; attempt++) {
            const s0 = starts[Math.floor(random() * starts.length)]
            const towards = pockets.length ? pockets[Math.floor(random() * pockets.length)] : { x: random() * width, y: random() * height }
            let p = { x: s0.x + 0.5, y: s0.y + 0.5 }
            let heading = Math.atan2(towards.y - p.y, towards.x - p.x) + (random() - 0.5) * 1.2
            const line = [p]
            const claimed: OrganicCavePoint[] = []
            const claim = (from: OrganicCavePoint, to: OrganicCavePoint) => {
                for (let y = Math.floor(Math.min(from.y, to.y) - 1); y <= Math.ceil(Math.max(from.y, to.y) + 1); y++) {
                    for (let x = Math.floor(Math.min(from.x, to.x) - 1); x <= Math.ceil(Math.max(from.x, to.x) + 1); x++) {
                        if (claimed.length >= spec.cells || !inside(x, y, 1) || !inBand(x, y)) continue
                        if (claimed.some(c => c.x === x && c.y === y)) continue
                        if (distanceToPolyline(x + 0.5, y + 0.5, [from, to]) < ORGANIC_VEIN_REACH) claimed.push({ x, y })
                    }
                }
            }
            claim(p, p)
            for (let step = 0; step < 40 && claimed.length < spec.cells; step++) {
                heading += (random() - 0.5) * 0.8
                const next = { x: p.x + Math.cos(heading) * 1.1, y: p.y + Math.sin(heading) * 1.1 }
                if (!inside(Math.floor(next.x), Math.floor(next.y), 2)) { heading += Math.PI / 2; continue }
                claim(p, next)
                line.push(next)
                p = next
            }
            if (claimed.length < spec.cells) continue
            claimed.forEach(c => (cells[c.x][c.y] = code))
            veins.push({ material: spec.material, points: line.map(q => ({ x: Math.round(q.x * 100) / 100, y: Math.round(q.y * 100) / 100 })) })
            break
        }
    }

    // What a pocket holds: crystals along its back wall (always, in a void), a still pool in its middle.
    const crystals: OrganicCaveFeatures["crystals"] = []
    const pools: OrganicCaveFeatures["pools"] = []
    pockets.forEach(pocket => {
        const backWall = pocket.cells.filter(c => cells[c.x][c.y - 1] !== OrganicRock.Pocket)
        if ((pocket.void || random() < 0.6) && backWall.length) {
            const count = (pocket.void ? 2 : 1) + Math.floor(random() * Math.min(2, backWall.length))
            const used = new Set<number>()
            for (let i = 0; i < count * 3 && used.size < count; i++) {
                const c = backWall[Math.floor(random() * backWall.length)]
                if (used.has(c.x)) continue
                used.add(c.x)
                crystals.push({ x: c.x + 0.3 + random() * 0.4, y: c.y + 0.12, scale: Math.round((0.6 + random() * 0.4) * 100) / 100 })
            }
        }
        const middle = pocket.cells.filter(c => [[1, 0], [-1, 0], [0, 1], [0, -1]]
            .every(([dx, dy]) => cells[c.x + dx]?.[c.y + dy] === OrganicRock.Pocket) &&
            cells[c.x][c.y - 1] === OrganicRock.Pocket)
        if (!pocket.void && random() < 0.5 && middle.length) {
            const c = middle[Math.floor(random() * middle.length)]
            pools.push({ x: c.x + 0.5, y: c.y + 0.5, rx: 0.65, ry: 0.27 })
        }
    })

    return {
        width,
        height,
        cells,
        landing: centre,
        pockets,
        faceHeight: ORGANIC_FACE_HEIGHT,
        veins,
        crystals,
        pools,
    }
}

/**
 * Blocks to break from `start` to reach each cell (0-1 BFS: rock costs one, open ground and pockets nothing,
 * bedrock and the mouth are walls) -- the ember mine's own measure (mine_director computeMiningDepths).
 */
const miningDepths = (cells: string[][], width: number, height: number, start: OrganicCavePoint): number[] => {
    const depth = new Array(width * height).fill(Infinity)
    const passable = (x: number, y: number) => x >= 0 && y >= 0 && x < width && y < height &&
        cells[x][y] !== OrganicRock.Bedrock && cells[x][y] !== OrganicRock.Mouth
    const queue: number[] = [start.y * width + start.x]
    depth[queue[0]] = 0
    while (queue.length) {
        const i = queue.shift()!
        const x = i % width, y = Math.floor(i / width)
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy
            if (!passable(nx, ny)) continue
            const cost = organicRockIsBreakable(cells[nx][ny]) ? 1 : 0
            const j = ny * width + nx, d = depth[i] + cost
            if (d >= depth[j]) continue
            depth[j] = d
            if (cost) queue.push(j)
            else queue.unshift(j)
        }
    }
    return depth
}

/**
 * The mine's rewards, read straight off the plan: every ore cell pays one of its mineral, everything else is
 * barren (null). Keyed "x,y" like mine_director's MinePlan.rewards.
 */
export const organicMineRewards = (plan: OrganicCavePlan):
    Record<string, { material: OrganicOre; quantity: number; seamId: string } | null> => {
    const rewards: Record<string, { material: OrganicOre; quantity: number; seamId: string } | null> = {}
    for (let y = 0; y < plan.height; y++) for (let x = 0; x < plan.width; x++) {
        const code = plan.cells[x][y]
        if (!organicRockIsBreakable(code)) continue
        const material = organicOreAt(code)
        rewards[`${x},${y}`] = material ? { material, quantity: 1, seamId: `organic-${material}` } : null
    }
    return rewards
}
